import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import Loader from "react-loader-spinner";
import { Card, Col, Form, Row, Table } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { CSVLink } from "react-csv";
import { AuthContext } from "../../context/auth";
import CustomStyle from "../../styles";
import { getWarehouses } from "services/warehouseApis";
import { getProductsReport } from "services/ourProducts";

const LIMIT = 50;

const reportCsvHeaders = [
  { label: "Store", key: "store" },
  { label: "Store code", key: "storeCode" },
  { label: "Product", key: "name" },
  { label: "Item ID", key: "itemId" },
  { label: "Department", key: "department" },
  { label: "Barcode", key: "barcode" },
  { label: "Qty", key: "quantity" },
  { label: "Stock updated", key: "stockSyncedAt" },
  { label: "Catalog updated", key: "productSyncedAt" },
];

function ProductsReport() {
  const context = useContext(AuthContext);
  const token = context.authState?.token;
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [store, setStore] = useState("");
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadWarehouses = async () => {
      const response = await getWarehouses(token);
      if (cancelled) return;
      const list =
        response?.status && Array.isArray(response.data) ? response.data : [];
      const sorted = [...list].sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      );
      setWarehouses(sorted);
      if (!sorted[0]?.code) setLoading(false);
      setStore((current) => current || sorted[0]?.code || "");
    };

    loadWarehouses();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!store) return undefined;

    let cancelled = false;

    const loadReport = async () => {
      setLoading(true);
      const report = await getProductsReport(token, page, LIMIT, store);
      if (cancelled) return;
      setLoading(false);
      if (!report.status) {
        setRows([]);
        setTotal(0);
        setTotalPages(0);
        return;
      }
      setRows(report.data.results || []);
      setTotal(report.data.total || 0);
      setTotalPages(report.data.totalPages || 0);
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [token, page, store]);

  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const to = Math.min(page * LIMIT, total);
  const csvRows = rows.map((item) => ({
    store: item.storeName || "",
    storeCode: item.storeCode || "",
    name: item.name || "",
    itemId: item.itemId || "",
    department: item.department || "",
    barcode: item.barcode || "",
    quantity: item.quantity ?? "",
    stockSyncedAt: item.stockSyncedAt
      ? moment(item.stockSyncedAt).format("DD/MM/YYYY HH:mm")
      : "",
    productSyncedAt: item.productSyncedAt
      ? moment(item.productSyncedAt).format("DD/MM/YYYY HH:mm")
      : "",
  }));

  const handleStoreChange = (event) => {
    setStore(event.target.value);
    setPage(1);
  };

  return (
    <Row>
      <Col md="12">
        <Card className="strpied-tabled-with-hover">
          <Card.Header>
            <Row>
              <Col md="7">
                <Card.Title as="h4">Products Report</Card.Title>
                <p className="card-category">
                  Products currently shown on the app, with inventory and last
                  sync time{" "}
                  <span className="text-info">
                    (Showing {from} to {to} of {total})
                  </span>
                </p>
                <CSVLink
                  data={csvRows}
                  headers={reportCsvHeaders}
                  filename="products-report.csv"
                  className="btn btn-primary btn-sm card-category"
                >
                  Export CSV
                </CSVLink>
              </Col>
              <Col md="5">
                <Form.Group>
                  <label>Store</label>
                  <Form.Control
                    as="select"
                    value={store}
                    onChange={handleStoreChange}
                  >
                    {warehouses.map((warehouse) => (
                      <option key={warehouse.code} value={warehouse.code}>
                        {warehouse.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="table-full-width table-responsive px-0">
            {loading ? (
              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                  padding: "20px 0px",
                }}
              >
                <Loader
                  type="Oval"
                  color={CustomStyle.colors.primary}
                  height={50}
                  width={50}
                />
              </div>
            ) : (
              <Table className="table-hover table-striped">
                <thead>
                  <tr>
                    <th className="border-0">Store</th>
                    <th className="border-0">Product</th>
                    <th className="border-0">Barcode</th>
                    <th className="border-0">Qty</th>
                    <th className="border-0">Stock updated</th>
                    <th className="border-0">Catalog updated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((item) => (
                      <tr key={`${item.storeCode}-${item.productId}`}>
                        <td>
                          {item.storeName}
                          <br />
                          <small className="text-muted">{item.storeCode}</small>
                        </td>
                        <td>
                          {item.name}
                          <br />
                          <small className="text-muted">
                            {item.itemId}
                            {item.department ? ` · ${item.department}` : ""}
                          </small>
                        </td>
                        <td>{item.barcode}</td>
                        <td>{item.quantity}</td>
                        <td>
                          {moment(item.stockSyncedAt).format(
                            "DD/MM/YYYY HH:mm",
                          )}
                          <br />
                          <small className="text-muted">
                            {moment(item.stockSyncedAt).fromNow()}
                          </small>
                        </td>
                        <td>
                          {moment(item.productSyncedAt).format(
                            "DD/MM/YYYY HH:mm",
                          )}
                          <br />
                          <small className="text-muted">
                            {moment(item.productSyncedAt).fromNow()}
                          </small>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        style={{
                          textAlign: "center",
                          color: CustomStyle.colors.danger,
                        }}
                      >
                        <small>No products on the app</small>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
          <Card.Footer>
            {total > 0 ? (
              <ReactPaginate
                previousLabel={<i className="nc-icon nc-stre-left"></i>}
                nextLabel={<i className="nc-icon nc-stre-right"></i>}
                breakLabel={"..."}
                breakClassName={"break-me"}
                pageCount={totalPages}
                forcePage={page - 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={(data) => setPage(data.selected + 1)}
                containerClassName={"pagination"}
                activeClassName={"active"}
              />
            ) : null}
          </Card.Footer>
        </Card>
      </Col>
    </Row>
  );
}

export default ProductsReport;
